import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { stepDisplayLabel } from "@lib/selectionSteps";
import { TERMINAL_STAGES } from "@lib/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

interface MatrixStepColumn {
  id: string;
  continuityId: string;
  stepType: string | null;
  label: string | null;
  position: number;
}

interface MatrixStepRow {
  id: string;
  continuityId: string | null;
  status: string;
  outcome: string;
  rating: number | null;
}

interface MatrixContribution {
  candidateSelectionStepId: string | null;
}

interface MatrixCandidate {
  id: string;
  stage: string;
  candidate: { id: string; fullName: string; referenceNumber?: number };
  candidateSelectionSteps: MatrixStepRow[];
  interviewContributions: MatrixContribution[];
  // The reject/hold step the journey halted at (#3168), when it has.
  selectionJourneyHalt?: { step: { id: string } } | null;
}

interface InterviewsOverviewMatrixProps {
  campaignCandidates: MatrixCandidate[];
  steps: MatrixStepColumn[];
  onJumpToStep: (continuityId: string) => void;
}

type CellState = "active" | "advanced" | "rejected" | "withdrawn" | "hold";

const CELL_STYLES: Record<CellState, string> = {
  advanced: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  withdrawn: "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
  hold: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  active: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
};

const CELL_LABELS: Record<CellState, string> = {
  active: "In progress",
  advanced: "Advanced",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
  hold: "Hold",
};

function terminalCellState(candidateStage: string): CellState {
  return candidateStage === "withdrawn" ? "withdrawn" : "rejected";
}

function cellStateFor(
  step: MatrixStepRow | undefined,
  candidateStage: string,
  isStoppedStep: boolean,
): { state: CellState; rating: number | null } | null {
  if (!step) return null;
  const isTerminal = (TERMINAL_STAGES as readonly string[]).includes(candidateStage);
  if (step.status !== "completed") {
    if (!isTerminal) return { state: "active", rating: null };
    return isStoppedStep ? { state: terminalCellState(candidateStage), rating: null } : null;
  }
  switch (step.outcome) {
    case "advance":
      return { state: "advanced", rating: step.rating };
    case "reject":
      return { state: "rejected", rating: step.rating };
    case "hold":
      return { state: "hold", rating: step.rating };
    default:
      if (!isTerminal) return { state: "active", rating: null };
      return isStoppedStep ? { state: terminalCellState(candidateStage), rating: null } : null;
  }
}

// Index of the column a live candidate's journey halted at (#3168), or -1.
// Columns after it whose row is still pending are "not reached", not active.
function haltedColumnIndexFor(cc: MatrixCandidate, steps: MatrixStepColumn[]): number {
  const haltStepId = cc.selectionJourneyHalt?.step.id;
  if (!haltStepId) return -1;
  const row = cc.candidateSelectionSteps.find((s) => s.id === haltStepId);
  if (!row?.continuityId) return -1;
  return steps.findIndex((step) => step.continuityId === row.continuityId);
}

function stoppedAtContinuityIdFor(cc: MatrixCandidate, steps: MatrixStepColumn[]): string | null {
  if (!(TERMINAL_STAGES as readonly string[]).includes(cc.stage)) return null;
  for (const step of steps) {
    const row = cc.candidateSelectionSteps.find((s) => s.continuityId === step.continuityId);
    if (row && row.status !== "completed") return step.continuityId;
  }
  return null;
}

export function InterviewsOverviewMatrix({
  campaignCandidates,
  steps,
  onJumpToStep,
}: InterviewsOverviewMatrixProps) {
  const [collapsed, setCollapsed] = useState(false);
  if (campaignCandidates.length === 0 || steps.length === 0) return null;

  const orderedCandidates = [
    ...campaignCandidates.filter((cc) => cc.stage === "interview"),
    ...campaignCandidates.filter((cc) => cc.stage !== "interview"),
  ];

  const stoppedAtByCandidate = new Map<string, string | null>(
    orderedCandidates.map((cc) => [cc.id, stoppedAtContinuityIdFor(cc, steps)]),
  );
  const haltedColumnByCandidate = new Map<string, number>(
    orderedCandidates.map((cc) => [cc.id, haltedColumnIndexFor(cc, steps)]),
  );

  const stateForCell = (cc: MatrixCandidate, step: MatrixStepColumn, columnIndex: number) => {
    const row = cc.candidateSelectionSteps.find((s) => s.continuityId === step.continuityId);
    const haltedAt = haltedColumnByCandidate.get(cc.id) ?? -1;
    if (haltedAt >= 0 && columnIndex > haltedAt && row?.status !== "completed") return null;
    const isStopped = stoppedAtByCandidate.get(cc.id) === step.continuityId;
    return cellStateFor(row, cc.stage, isStopped);
  };

  const columnCounts = steps.map((step, columnIndex) => {
    const counts = {
      advanced: 0,
      rejected: 0,
      withdrawn: 0,
      hold: 0,
      inProgress: 0,
      notReached: 0,
    };
    for (const cc of orderedCandidates) {
      const state = stateForCell(cc, step, columnIndex);
      if (!state) counts.notReached += 1;
      else if (state.state === "advanced") counts.advanced += 1;
      else if (state.state === "rejected") counts.rejected += 1;
      else if (state.state === "withdrawn") counts.withdrawn += 1;
      else if (state.state === "hold") counts.hold += 1;
      else counts.inProgress += 1;
    }
    return counts;
  });

  return (
    <div
      data-testid="interviews-overview-matrix"
      className="mb-4 overflow-hidden rounded-lg border border-border bg-card-alt"
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <button
          type="button"
          aria-label={collapsed ? "Expand interviews overview" : "Collapse interviews overview"}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 text-xs font-medium text-text-secondary"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          Campaign overview
        </button>
        <span className="text-[10px] text-text-faint">
          {orderedCandidates.length} candidate{orderedCandidates.length === 1 ? "" : "s"} ·{" "}
          {steps.length} step{steps.length === 1 ? "" : "s"}
        </span>
      </div>
      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-xs">
            <thead>
              <tr className="bg-card">
                <th className="sticky left-0 z-10 bg-card px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  Candidate
                </th>
                {steps.map((step) => (
                  <th
                    key={step.continuityId}
                    className="border-l border-border px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-text-muted"
                  >
                    {stepDisplayLabel(step)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orderedCandidates.map((cc) => (
                <tr
                  key={cc.id}
                  data-testid={`matrix-row-${cc.id}`}
                  className={`border-t border-border ${cc.stage !== "interview" ? "opacity-60" : ""}`}
                >
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-card-alt px-3 py-2 text-left text-xs font-medium text-text-primary"
                  >
                    {typeof cc.candidate.referenceNumber === "number" && (
                      <>
                        <CandidateReferenceBadge
                          referenceNumber={cc.candidate.referenceNumber}
                        />{" "}
                      </>
                    )}
                    {cc.candidate.fullName}
                  </th>
                  {steps.map((step, columnIndex) => {
                    const row = cc.candidateSelectionSteps.find(
                      (s) => s.continuityId === step.continuityId,
                    );
                    const state = stateForCell(cc, step, columnIndex);
                    const contribCount = row
                      ? cc.interviewContributions.filter(
                          (c) => c.candidateSelectionStepId === row.id,
                        ).length
                      : 0;
                    return (
                      <td key={step.continuityId} className="border-l border-border align-top">
                        <button
                          type="button"
                          onClick={() => onJumpToStep(step.continuityId)}
                          data-testid={`matrix-cell-${cc.id}-${step.continuityId}`}
                          className="flex w-full flex-col items-start gap-1 px-2 py-2 text-left hover:bg-card-hover"
                        >
                          {state ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${CELL_STYLES[state.state]}`}
                            >
                              {CELL_LABELS[state.state]}
                              {state.rating != null && (
                                <span className="ml-1 text-[10px]">{state.rating}/5</span>
                              )}
                            </span>
                          ) : (
                            <span
                              className="text-[10px] text-text-faint"
                              data-testid={`matrix-cell-empty-${cc.id}-${step.continuityId}`}
                            >
                              —
                            </span>
                          )}
                          {contribCount > 0 && (
                            <span className="text-[10px] text-text-muted">
                              {contribCount} note{contribCount === 1 ? "" : "s"}
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border bg-card">
                <td className="sticky left-0 z-10 bg-card px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  Totals
                </td>
                {columnCounts.map((counts, i) => (
                  <td
                    key={steps[i].continuityId}
                    className="border-l border-border px-2 py-2 text-[10px] text-text-muted"
                    data-testid={`matrix-footer-${steps[i].continuityId}`}
                  >
                    {counts.advanced > 0 && (
                      <div>
                        <span className="font-medium text-green-700 dark:text-green-400">
                          {counts.advanced}
                        </span>{" "}
                        advanced
                      </div>
                    )}
                    {counts.rejected > 0 && (
                      <div>
                        <span className="font-medium text-red-700 dark:text-red-400">
                          {counts.rejected}
                        </span>{" "}
                        rejected
                      </div>
                    )}
                    {counts.withdrawn > 0 && (
                      <div>
                        <span className="font-medium text-gray-500 dark:text-gray-400">
                          {counts.withdrawn}
                        </span>{" "}
                        withdrawn
                      </div>
                    )}
                    {counts.hold > 0 && (
                      <div>
                        <span className="font-medium text-amber-700 dark:text-amber-400">
                          {counts.hold}
                        </span>{" "}
                        hold
                      </div>
                    )}
                    {counts.inProgress > 0 && (
                      <div>
                        <span className="font-medium text-blue-700 dark:text-blue-400">
                          {counts.inProgress}
                        </span>{" "}
                        in progress
                      </div>
                    )}
                    {counts.notReached > 0 && (
                      <div>
                        <span className="text-text-faint">{counts.notReached}</span> not yet
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
